import type { ComponentType, ReactNode } from 'react'
// @ts-ignore // needed due to noResolve for tsc
import type { ExtensionPoint, ExtensionConfig, ExtensionsConfig, Extensions, ExtensionPoints, FetchWithTimeout, FrameworkVars, NavBadgeProps, ReadonlyTsgProperties, OverrideLeftNavOption, RuntimeValidator, SparkyAvailFunc, FeatureFlagOptions, ServiceOptions, SparkyFramework, StateRef, StoreState, Vars, TsgProperty, ValidationResult, NavItem } from '../framework/types'

export type {
  ExtensionsConfig,
  Extensions,
  ExtensionPoints,
  ExtensionPoint,
  ExtensionConfig,
  FetchWithTimeout,
  FrameworkVars,
  SparkyAvailFunc,
  SparkyFramework,
  StateRef,
  StoreState,
}
export type IsAllowed = () => boolean | undefined
export type NavLazyFactory<T> = () => Promise<{ default: ComponentType<T> }>
export type NavLazyComponent<T> = ComponentType<T> | NavLazyFactory<T>

const validateIsAllowed = (isAllowed: any) => !(
  isAllowed != null && typeof isAllowed !== 'function' && typeof isAllowed !== 'boolean'
)
const isStringOrStrings = (value: any): value is string | string[] => {
  if (Array.isArray(value)) {
    return value.every(item => typeof item === 'string')
  }
  return typeof value === 'string'
}
const isRegExp = (value?: any): value is RegExp => {
  return Object.prototype.toString.call(value) === '[object RegExp]'
}

export type SparkyLibsObj = Record<string, unknown>
export type SparkyLibsExport = SparkyLibsObj & { default?: SparkyLibsObj }
export type SparkyLibsFn = () => SparkyLibsExport | Promise<SparkyLibsExport>
export type SparkyLibExtConfig = SparkyLibsExport | SparkyLibsFn

export const SPARKY_LIBS_EXTPOINT = {
  id: 'sparky.internal.libs.global',
  version: '0.0.1',
  compatibilityRange: '~0.0.1',
  isSingleton: false,
  description: 'inject libs to global sparky libs namespace',
  validator: {
    validate(config) {
      const pass = (typeof config === 'function') ||
        (typeof config === 'object' && Object.keys(config).length > 0) ||
        (config?.default && Object.keys(config.default).length > 0)
      return {
        success: pass,
        error: pass ? undefined : 'libs must be a non-empty object or a function that returns a non-empty object',
      }
    }
  }
} as ExtensionPoint<SparkyLibExtConfig>

export type SparkyMfeVersionMeta = {
  name: string
  id: string
  version: string
  vars: Vars | undefined
}
export type SparkyMfeVersionConfig = {
  name: string
  select: (metas: SparkyMfeVersionMeta[], state: StoreState) => SparkyMfeVersionMeta | string | null | undefined
  isAllowed?: IsAllowed
}

export const SPARKY_MFE_TSG_VERSION_SELECT_EXTPOINT = {
  id: 'sparky.mfe.tsg.versions.select',
  version: '0.0.1',
  compatibilityRange: '~0.0.1',
  isSingleton: false,
  description: 'specify micro-app version or disable all versions',
  validator: {
    validate(config) {
      if (typeof config.name !== 'string') {
        return {
          success: false,
          error: 'name of the micro-app must be a string',
        }
      }
      if (typeof config.select !== 'function') {
        return {
          success: false,
          error: 'select must be a function',
        }
      }
      if (!validateIsAllowed(config.isAllowed)) {
        return {
          success: false,
          error: 'optional isAllowed must be a function',
        }
      }
      return {
        success: true,
      }
    }
  }
} as ExtensionPoint<SparkyMfeVersionConfig>

export type SparkyMfePrefilterConfig = {
  names?: string[] | string
  filter: (meta: SparkyMfeVersionMeta, state: StoreState, allMeta: SparkyMfeVersionMeta[]) => boolean | undefined
  isAllowed?: IsAllowed
}

export const SPARKY_MFE_TSG_PREFILTER_EXTPOINT = {
  id: 'sparky.mfe.tsg.mfe.prefilter',
  version: '0.0.1',
  compatibilityRange: '~0.0.1',
  isSingleton: false,
  description: 'filter micro-app versions before run the version select',
  validator: {
    validate(config) {
      if (config.names && !isStringOrStrings(config.names)) {
        return {
          success: false,
          error: 'name of the micro-app must be a string',
        }
      }
      if (typeof config.filter !== 'function') {
        return {
          success: false,
          error: 'select must be a function',
        }
      }
      if (!validateIsAllowed(config.isAllowed)) {
        return {
          success: false,
          error: 'optional isAllowed must be a function',
        }
      }
      return {
        success: true,
      }
    }
  }
} as ExtensionPoint<SparkyMfePrefilterConfig>

export type NavFlags = Record<string, boolean | string | number | null | undefined> & {
  bypassTsgAvail?: boolean
  bypassGlobalAvail?: boolean
}

export type SparkyNavRouteConfig<T = any> = {
  path: string
  component: NavLazyComponent<T>
  contentClassName?: string
  mapStateToProps?: (state: StoreState) => Partial<T>
  stateLoaders?: string[]
  replacePaths?: string | string[]
  isAllowed?: IsAllowed
  fallbackRedirect?: string | (() => string | undefined)
  flags?: NavFlags & {
    lazy?: boolean
    exact?: boolean
    autoCollapse?: boolean
  }
}

const navRouteValidator = {
  validate(config) {
    if (!config) {
      return {
        success: false,
        error: 'route configuration cannot be empty',
      }
    }
    if (typeof config.path !== 'string') {
      return {
        success: false,
        error: 'path must be a string and required',
      }
    }
    if (typeof config.component !== 'function') {
      return {
        success: false,
        error: 'component must be a function or class/function ReactComponent and required',
      }
    }
    if (config.contentClassName && typeof config.contentClassName !== 'string') {
      return {
        success: false,
        error: 'optional contentClassName must be a string',
      }
    }
    if (config.mapStateToProps && typeof config.mapStateToProps !== 'function') {
      return {
        success: false,
        error: 'optional mapStateToProps must be a function',
      }
    }
    if (config.stateLoaders && !isStringOrStrings(config.stateLoaders)) {
      return {
        success: false,
        error: 'optional stateLoaders must be an array of strings',
      }
    }
    if (!validateIsAllowed(config.isAllowed)) {
      return {
        success: false,
        error: 'optional isAllowed must be a function',
      }
    }
    if (config.fallbackRedirect && typeof config.fallbackRedirect !== 'string' && typeof config.fallbackRedirect !== 'function') {
      return {
        success: false,
        error: 'optional fallbackRedirect must be a string or a function',
      }
    }
    if (config.replacePaths && !isStringOrStrings(config.replacePaths)) {
      return {
        success: false,
        error: 'optional replacePaths must be a string or an array of strings',
      }
    }
    if (config.flags && typeof config.flags !== 'object') {
      return {
        success: false,
        error: 'optional flags must be an object',
      }
    }
    return {
      success: true,
    }
  }
} as RuntimeValidator<SparkyNavRouteConfig>

export const SPARKY_NAV2_ROUTES_EXTPOINT = {
  id: 'sparky.nav.v2.routes',
  version: '0.0.1',
  compatibilityRange: '~0.0.1',
  isSingleton: false,
  description: 'add routes to sparky for nav v2',
  validator: navRouteValidator,
} as ExtensionPoint<SparkyNavRouteConfig>

export const SPARKY_NAV3_ROUTES_EXTPOINT = {
  id: 'sparky.nav.v3.routes',
  version: '0.0.1',
  compatibilityRange: '~0.0.1',
  isSingleton: false,
  description: 'add routes to sparky for nav v3',
  validator: navRouteValidator,
} as ExtensionPoint<SparkyNavRouteConfig>

export type SparkyNavRedirectConfig = {
  path: string
  redirectTo: string | (() => string)
  push?: boolean
  exact?: boolean
  preserveSearch?: boolean
  isAllowed?: IsAllowed
}
const navRouteRedirectValidator = {
  validate(config) {
    if (!config) {
      return {
        success: false,
        error: 'redirect configuration cannot be empty',
      }
    }
    if (typeof config.path !== 'string') {
      return {
        success: false,
        error: 'path must be a string and required',
      }
    }
    if (typeof config.redirectTo !== 'string' && typeof config.redirectTo !== 'function') {
      return {
        success: false,
        error: 'redirectTo must be a string or function return a string and required',
      }
    }
    if (config.push != null && typeof config.push !== 'boolean') {
      return {
        success: false,
        error: 'optional push must be a boolean',
      }
    }
    if (config.exact != null && typeof config.exact !== 'boolean') {
      return {
        success: false,
        error: 'optional exact must be a boolean',
      }
    }
    if (config.preserveSearch != null && typeof config.preserveSearch !== 'boolean') {
      return {
        success: false,
        error: 'optional preserveSearch must be a boolean',
      }
    }
    if (!validateIsAllowed(config.isAllowed)) {
      return {
        success: false,
        error: 'optional isAllowed must be a function',
      }
    }
    return {
      success: true,
    }
  }
} as RuntimeValidator<SparkyNavRedirectConfig>

export const SPARKY_NAV3_REDIRECTS_EXTPOINT = {
  id: 'sparky.nav.v3.redirects',
  version: '0.0.1',
  compatibilityRange: '~0.0.1',
  isSingleton: false,
  description: 'add redirect route to sparky for nav v3',
  validator: navRouteRedirectValidator,
} as ExtensionPoint<SparkyNavRedirectConfig>

export type ForbiddenKeys = 'component' | 'mapStateToProps' | 'hoistChildren' | 'navPath' | 'overrideLeftNav' | 'overrideItemForDisplay' | 'lazy' | 'className' | 'contentClassName' | 'isLabel' | 'asPlaceholder' | 'callAvail' | 'isLoading' | 'redirectTo' | 'availFallbackRedirect' | 'autoCollapse'
export type LeftNavItem = ({
  path: string
  key?: string
} | {
  path?: string
  key: string
} | {
  path: string
  key: string
}) & {
  title: (() => string) | string
  titleComponent?: NavLazyComponent<any>
  children?: LeftNavItem[]
  disabled?: boolean | (() => boolean) // render item as disabled, not clickable
  avail?: SparkyAvailFunc // can be a object and combine with mergeAvail, availFallbackRedirect.
  dataMetrics?: string
  badge?: () => NavBadgeProps | undefined
  flags?: NavFlags
} & {
  [K in string as K extends ForbiddenKeys ? never : K]: any
}
export type NavIcon = ComponentType | (() => ReactNode) | string
export type TopLeftNavItem = LeftNavItem & {
  overrideItemForDisplay?: (item: LeftNavItem) => LeftNavItem | undefined
  hoistChildren?: boolean // automatically promote children at top level (parent level)
  overrideLeftNav?: OverrideLeftNavOption
  icon?: NavIcon
}
export type SparkyLeftNavConfig = {
  stateLoaders?: string[]
  navigation: () => TopLeftNavItem[]
  isAllowed?: IsAllowed
}
export type SparkyBottomLeftNavConfig<T = any> = LeftNavItem & {
  isAllowed: IsAllowed
  stateLoaders?: string[]
  icon?: NavIcon
} & ({
  navComponent: NavLazyComponent<T>
  mapStateToProps: (state: StoreState) => T
} | {
  children?: LeftNavItem[] | (() => LeftNavItem[])
})

export const leftNavValidtor = {
  validate(config) {
    if (!config) {
      return {
        success: false,
        error: 'top left nav configuration cannot be empty',
      }
    }
    if (typeof config.navigation !== 'function') {
      return {
        success: false,
        error: 'navigation must be a function and required',
      }
    }
    if (!validateIsAllowed(config.isAllowed)) {
      return {
        success: false,
        error: 'optional isAllowed must be a function',
      }
    }
    return {
      success: true,
    }
  }
} as RuntimeValidator<SparkyLeftNavConfig>

export const SPARKY_NAV3_LEFT_NAV_EXTPOINT = {
  id: 'sparky.nav.v3.left.items',
  version: '0.0.1',
  compatibilityRange: '~0.0.1',
  isSingleton: false,
  description: 'merge navigation tree to sparky for left nav v3',
  validator: leftNavValidtor,
} as ExtensionPoint<SparkyLeftNavConfig>

export const SPARKY_NAV3_LEFT_NAV_TPL_EXTPOINT = {
  id: 'sparky.nav.v3.left.tpl',
  version: '0.0.1',
  compatibilityRange: '~0.0.1',
  isSingleton: true,
  description: 'template for navigation tree to sparky for left nav v3',
  validator: leftNavValidtor,
} as ExtensionPoint<SparkyLeftNavConfig>

export type DynamicLeftNavItem = LeftNavItem & ({
  children: DynamicLeftNavItem[]
  dynamicChildren?: never
} | {
  dynamicChildren: () => LeftNavItem[]
  children?: never
})
export type SparkyDynamicTopLeftNavConfig = SparkyLeftNavConfig & DynamicLeftNavItem & {
  isAllowed: IsAllowed
  stateLoaders?: string[]
  navComponent: never
  mapStateToProps: never
}

const validateLeftNavBase = (config: LeftNavItem) => {
  if (!config) {
    return {
      success: false,
      error: 'top left nav configuration cannot be empty',
    }
  }
  if (!config.key && !config.path) {
    return {
      success: false,
      error: 'either key or path is required for a left nav item'
    }
  }
  if (config.key && typeof config.key !== 'string') {
    return {
      success: false,
      error: 'key must be a string if provided'
    }
  }
  if (config.path && typeof config.path !== 'string') {
    return {
      success: false,
      error: 'path must be a string if provided'
    }
  }
  if (!config.title || (typeof config.title !== 'string' && typeof config.title !== 'function')) {
    return {
      success: false,
      error: 'title must be a string or a function and required'
    }
  }
  if (config.disabled != null && typeof config.disabled !== 'boolean' && typeof config.disabled !== 'function') {
    return {
      success: false,
      error: 'disabled must be a boolean or a function return boolean'
    }
  }
  if (config.avail != null && typeof config.avail !== 'function') {
    return {
      success: false,
      error: 'avail must be a function return boolean'
    }
  }
  if (config.badge && typeof config.badge !== 'function') {
    return {
      success: false,
      error: 'badge must be a function returning NavBadgeProps or undefined'
    }
  }
  if (config.component) {
    return {
      success: false,
      error: 'component is not allowed in dynamic left nav item'
    }
  }
}
const validateDirectLeftNavCommon = (config: SparkyBottomLeftNavConfig) => {
  if (!config) {
    return {
      success: false,
      error: 'top left nav configuration cannot be empty',
    }
  }
  const result = validateLeftNavBase(config)
  if (result) {
    return result
  }
  if (config.stateLoaders && !isStringOrStrings(config.stateLoaders)) {
    return {
      success: false,
      error: 'optional stateLoaders must be an array of strings',
    }
  }
  if (!validateIsAllowed(config.isAllowed)) {
    return {
      success: false,
      error: 'optional isAllowed must be a function',
    }
  }
}

export const SPARKY_NAV3_DYNAMIC_TOP_LEFT_EXTPOINT = {
  id: 'sparky.nav.v3.dynamic.left.top',
  version: '0.0.1',
  compatibilityRange: '~0.0.1',
  isSingleton: false,
  description: 'merge navigation dynamic sub-menu to sparky for bottom left nav v3',
  validator: {
    validate(config) {
      const baseResult = validateDirectLeftNavCommon(config)
      if (baseResult) {
        return baseResult
      }
      const traverse = (item: DynamicLeftNavItem): ValidationResult | undefined => {
        const result = validateLeftNavBase(item)
        if (result) {
          return result
        }
        if (item.dynamicChildren && typeof item.dynamicChildren !== 'function') {
          return {
            success: false,
            error: 'dynamicChildren must be a function return an array of left nav item'
          }
        }
        if (item.children) {
          if (!Array.isArray(item.children) || item.children.length !== 1) {
            return {
              success: false,
              error: 'children must be an array with exactly one item for a dynamic left nav item'
            }
          }
          if (item.dynamicChildren) {
            return {
              success: false,
              error: 'dynamicChildren and children cannot be used together for a dynamic left nav item'
            }
          }
          const result = traverse(item.children[0] as DynamicLeftNavItem)
          if (result) {
            return result
          }
        }
        if (!item.children && !item.dynamicChildren) {
          return {
            success: false,
            error: 'either dynamicChildren or children is required for a dynamic left nav item'
          }
        }
        if (item.navComponent) {
          return {
            success: false,
            error: 'navComponent is not allowed for dynamic left nav item',
          }
        }
        if (item.mapStateToProps) {
          return {
            success: false,
            error: 'mapStateToProps is not allowed',
          }
        }
      }
      const traverseResult = traverse(config)
      if (traverseResult) {
        return traverseResult
      }
      return {
        success: true,
      }
    }
  },
} as ExtensionPoint<SparkyDynamicTopLeftNavConfig>

const bottomLeftNavValidator = {
  validate(config) {
    const result = validateDirectLeftNavCommon(config)
    if (result) {
      return result
    }
    if (config.navComponent && config.children) {
      return {
        success: false,
        error: 'navComponent and children not allowed to coexist in bottom left nav'
      }
    }
    if (config.navComponent && typeof config.navComponent !== 'function') {
      return {
        success: false,
        error: 'navComponent must be a function or class/function ReactComponent and required',
      }
    }
    if (Array.isArray(config.children) && config.children.length > 0 && !config.children.every(child =>
      typeof child === 'object' && typeof child.path === 'string'
    )) {
      return {
        success: false,
        error: 'children must be an array of LeftNavItem objects with a path property'
      }
    }
    if (config.mapStateToProps && typeof config.mapStateToProps !== 'function') {
      return {
        success: false,
        error: 'optional mapStateToProps must be a function',
      }
    }
    return {
      success: true,
    }
  }
} as RuntimeValidator<SparkyBottomLeftNavConfig>

export const SPARKY_NAV3_BOTTOM_LEFT_EXTPOINT = {
  id: 'sparky.nav.v3.left.bottom',
  version: '0.0.1',
  compatibilityRange: '~0.0.1',
  isSingleton: false,
  description: 'merge navigation item to sparky for bottom left nav v3',
  validator: bottomLeftNavValidator,
} as ExtensionPoint<SparkyBottomLeftNavConfig>

export const SPARKY_NAV3_BOTTOM_MOST_LEFT_EXTPOINT = {
  id: 'sparky.nav.v3.left.bottom.most',
  version: '0.0.1',
  compatibilityRange: '~0.0.1',
  isSingleton: true,
  description: 'set navigation item to sparky for bottom most of left nav v3',
  validator: bottomLeftNavValidator,
} as ExtensionPoint<SparkyBottomLeftNavConfig>

export type SparkyDynamicBottomLeftNavConfig = SparkyBottomLeftNavConfig & {
  dynamicChildren: () => LeftNavItem[]
  children: never
  navComponent: never
  mapStateToProps: never
}

export const SPARKY_NAV3_DYNAMIC_BOTTOM_LEFT_EXTPOINT = {
  id: 'sparky.nav.v3.dynamic.left.bottom',
  version: '0.0.1',
  compatibilityRange: '~0.0.1',
  isSingleton: false,
  description: 'merge navigation dynamic sub-menu to sparky for bottom left nav v3',
  validator: {
    validate(config) {
      const result = validateDirectLeftNavCommon(config)
      if (result) {
        return result
      }
      if (config.children) {
        return {
          success: false,
          error: 'children are not allowed for dynamic bottom left nav item'
        }
      }
      if (!config.dynamicChildren || typeof config.dynamicChildren !== 'function') {
        return {
          success: false,
          error: 'required dynamicChildren must be a function return an array of left nav item'
        }
      }
      if (config.navComponent) {
        return {
          success: false,
          error: 'navComponent is not allowed for dynamic left nav item',
        }
      }
      if (config.mapStateToProps) {
        return {
          success: false,
          error: 'mapStateToProps is not allowed',
        }
      }
      return {
        success: true,
      }
    }
  },
} as ExtensionPoint<SparkyDynamicBottomLeftNavConfig>

export type FetcherParams = {
  state: StoreState
  stateRef: StateRef
  sparkyFramework: SparkyFramework
  fxVars: FrameworkVars
  fetch: FetchWithTimeout
}
export type SetDynamicFallbackProperyFn = <T = any>(prop: { key: string, source: string, value: T }) => boolean
export type GenerateParams = FetcherParams & {
  key: string
  tsg_id: string
  /**
   * properties before any fallbacks applied
   */
  properties: ReadonlyTsgProperties
  /**
   * Use this feature carefully! Avoid if possible!
   *
   * set additional props that prefixed with the current key.
   * e.g. set `netsec.features.12345` additional to `netsec.features`.
   * return false if the property not been set, true if set successfully.
   * return false when the key is not prefixed with current key or key already exists.
   */
  setDynamicFallbackPropery: SetDynamicFallbackProperyFn
}

export type MfePropertyFallbackConfig<T> = {
  /**
   * key of the MFE Property to provide fallback value
   * Please set the extension id same as the key
   */
  key: string
  /**
   * the fallback source of the MFE Property
   */
  source: string
  /**
   * optional sync flag to indicate whether the fallback value should be synchronously returned
   * if sync is true, and generate function return a promise, the promise will block micro-app render to ensure return a resolved value
   * if generate function does not return a promise, sync or not does not matter
   * by default, sync is false, if generate function return a promise, the promise will be returned instead without blocking micro-app render
   */
  sync?: boolean
  /**
   * @param params provide a fetch function with state, vars and framework
   * @returns the fallback value for the MFE property or a Promise that resolves to the fallback value
   */
  generate: (params: GenerateParams) => Promise<T | undefined> | T | undefined
  /**
   * optional isAllowed function to determine if the extension should be enabled
   */
  isAllowed?: IsAllowed
}

export const SPARKY_TSG_MFE_PROPERTY_FALLBACK_EXTPOINT = {
  id: 'sparky.mfe.prop.tsg.fallback',
  version: '0.0.1',
  compatibilityRange: '~0.0.1',
  isSingleton: false,
  description: 'add fallback fetch for tsg level mfe properties',
  validator: {
    validate(config) {
      if (!config) {
        return {
          success: false,
          error: 'mfe property fallback configuration cannot be empty',
        }
      }
      if (!config.key || typeof config.key !== 'string') {
        return {
          success: false,
          error: 'key is required and must be a string',
        }
      }
      if (!config.source || typeof config.source !== 'string') {
        return {
          success: false,
          error: 'source is required and must be a string',
        }
      }
      if (config.sync != null && typeof config.sync !== 'boolean') {
        return {
          success: false,
          error: 'optional sync and must be a boolean',
        }
      }
      if (typeof config.generate !== 'function') {
        return {
          success: false,
          error: 'generate is required and must be a function',
        }
      }
      if (!validateIsAllowed(config.isAllowed)) {
        return {
          success: false,
          error: 'optional isAllowed must be a function',
        }
      }
      return {
        success: true,
      }
    }
  },
} as ExtensionPoint<MfePropertyFallbackConfig<any>>

export type MfePropertyOverrideConfig<T> = {
  /**
   * key of the MFE Property to provide override value
   * Please set the extension id same as the key
   */
  key: string
  override: (prop: TsgProperty<T>, props: ReadonlyTsgProperties) => T | undefined
  /**
   * optional isAllowed function to determine if the extension should be enabled
   */
  isAllowed?: IsAllowed
}

export const SPARKY_TSG_MFE_PROPERTY_OVERRIDE_EXTPOINT = {
  id: 'sparky.mfe.prop.tsg.overrides',
  version: '0.0.1',
  compatibilityRange: '~0.0.1',
  isSingleton: false,
  description: 'add overrides mfe properties',
  validator: {
    validate(config) {
      if (!config) {
        return {
          success: false,
          error: 'mfe property overrides configuration cannot be empty',
        }
      }
      if (!config.key || typeof config.key !== 'string') {
        return {
          success: false,
          error: 'key is required and must be a string',
        }
      }
      if (typeof config.override !== 'function') {
        return {
          success: false,
          error: 'override is required and must be a function',
        }
      }
      if (!validateIsAllowed(config.isAllowed)) {
        return {
          success: false,
          error: 'optional isAllowed must be a function',
        }
      }
      return {
        success: true,
      }
    }
  },
} as ExtensionPoint<MfePropertyOverrideConfig<any>>

export type StateLoaderParams = FetcherParams & { name: string }
export type StateLoaderConfig<T = any> = {
  name: string
  requireFetch?: boolean
  loader: (params: StateLoaderParams) => T
  isAllowed?: IsAllowed
}

export const SPARKY_NAV3_TSG_STATE_LOADERS_EXTPOINT = {
  id: 'sparky.nav.v3.state.tsg.loaders',
  version: '0.0.1',
  compatibilityRange: '~0.0.1',
  isSingleton: false,
  description: 'add tsg store loaders for left nav v3',
  validator: {
    validate(config) {
      if (!config) {
        return {
          success: false,
          error: 'state loader configuration cannot be empty',
        }
      }
      if (!config.name || typeof config.name !== 'string') {
        return {
          success: false,
          error: 'name is required and must be a string',
        }
      }
      if (config.requireFetch != null && typeof config.requireFetch !== 'boolean') {
        return {
          success: false,
          error: 'optional requireFetch and must be a boolean',
        }
      }
      if (typeof config.loader !== 'function') {
        return {
          success: false,
          error: 'loader is required and must be a function',
        }
      }
      if (!validateIsAllowed(config.isAllowed)) {
        return {
          success: false,
          error: 'optional isAllowed must be a function',
        }
      }
      return {
        success: true,
      }
    }
  },
} as ExtensionPoint<StateLoaderConfig>

export type ExposesNs = Record<string, any>
export type TsgExposes = Record<string, ExposesNs | undefined>
export type SparkyTsgExposesConfig = {
  exposes: () => TsgExposes | null | undefined
  isAllowed?: IsAllowed
}

export const SPARKY_NAV3_TSG_EXPOSES_COMPAT_EXTPOINT = {
  id: 'sparky.nav.v3.tsg.exposes',
  version: '0.0.1',
  compatibilityRange: '~0.0.1',
  isSingleton: false,
  description: 'add tsg level exposes for backward compatible for left nav v3',
  validator: {
    validate(config) {
      if (!config) {
        return {
          success: false,
          error: 'exposes configuration cannot be empty',
        }
      }
      if (typeof config.exposes !== 'function') {
        return {
          success: false,
          error: 'exposes must be a function and required',
        }
      }
      if (!validateIsAllowed(config.isAllowed)) {
        return {
          success: false,
          error: 'optional isAllowed must be a function',
        }
      }
      return {
        success: true,
      }
    }
  }
} as ExtensionPoint<SparkyTsgExposesConfig>

export type ProviderMatcher = (name: string) => boolean | undefined
export type SparkyServiceUrlProviderConfig = {
  match?: string | string[] | RegExp | ProviderMatcher
  getServiceUrl: (name: string, options: ServiceOptions, state: StoreState) => string | undefined
  allowFallback?: boolean // allow fallback to other providers if current one return undefined
  isAllowed?: IsAllowed
}

export const SPARKY_SERVICE_URL_PROVIDER_EXTPOINT = {
  id: 'sparky.service_urls.providers',
  version: '0.0.1',
  compatibilityRange: '~0.0.1',
  isSingleton: false,
  description: 'provide name and url value for getServiceUrl method',
  validator: {
    validate(config) {
      if (!config) {
        return {
          success: false,
          error: 'service url provider configuration cannot be empty',
        }
      }
      if (!validateIsAllowed(config.isAllowed)) {
        return {
          success: false,
          error: 'optional isAllowed must be a function',
        }
      }
      if (config.match != null && !isStringOrStrings(config.match) &&
        !isRegExp(config.match) && typeof config.match !== 'function') {
        return {
          success: false,
          error: 'optional match must be a string, array of strings, regular expression, object with prefix property, or a function'
        }
      }
      if (!config.getServiceUrl || typeof config.getServiceUrl !== 'function') {
        return {
          success: false,
          error: 'getServiceUrl is required and must be a function',
        }
      }
      if (config.allowFallback != null && typeof config.allowFallback !== 'boolean') {
        return {
          success: false,
          error: 'optional allowFallback must be a boolean'
        }
      }
      return {
        success: true,
      }
    }
  }
} as ExtensionPoint<SparkyServiceUrlProviderConfig>

export type SparkyFeatureFlagProviderConfig = {
  match?: string | string[] | RegExp | ProviderMatcher
  fromService: string | string[]
  getFeatureFlag: <T = any>(name: string, options: FeatureFlagOptions<T>, state: StoreState) => T | undefined
  allowFallback?: boolean // allow fallback to other providers if current one return undefined
  isAllowed?: IsAllowed
  priority?: number
}

export const SPARKY_FEATURE_FLAG_PROVIDER_EXTPOINT = {
  id: 'sparky.feature_flags.providers',
  version: '0.0.1',
  compatibilityRange: '~0.0.1',
  isSingleton: false,
  description: 'provide name and value for getFeatureFlag method',
  validator: {
    validate(config) {
      if (!config) {
        return {
          success: false,
          error: 'feature flag provider configuration cannot be empty',
        }
      }
      if (!validateIsAllowed(config.isAllowed)) {
        return {
          success: false,
          error: 'optional isAllowed must be a function',
        }
      }
      if (config.match != null && !isStringOrStrings(config.match) &&
        !isRegExp(config.match) && typeof config.match !== 'function') {
        return {
          success: false,
          error: 'optional match must be a string, array of strings, regular expression, object with prefix property, or a function'
        }
      }
      if (!config.fromService || !isStringOrStrings(config.fromService)) {
        return {
          success: false,
          error: 'fromService is required and must be a string'
        }
      }
      if (!config.getFeatureFlag || typeof config.getFeatureFlag !== 'function') {
        return {
          success: false,
          error: 'getFeatureFlag is required and must be a function',
        }
      }
      if (config.allowFallback != null && typeof config.allowFallback !== 'boolean') {
        return {
          success: false,
          error: 'optional allowFallback must be a boolean'
        }
      }
      return {
        success: true,
      }
    }
  }
} as ExtensionPoint<SparkyFeatureFlagProviderConfig>

export type TsgAccessAvailConfig = {
  tsgAvail: () => boolean
  isAllowed?: IsAllowed
}
export const SPARKY_TSG_ACCESS_AVAIL_EXTPOINT = {
  id: 'sparky.tsg.access.avail',
  version: '0.0.1',
  compatibilityRange: '~0.0.1',
  isSingleton: false,
  description: 'tsg access availability configuration',
  validator: {
    validate(config) {
      if (!config) {
        return {
          success: false,
          error: 'tsg access availability configuration cannot be empty',
        }
      }
      if (!validateIsAllowed(config.isAllowed)) {
        return {
          success: false,
          error: 'optional isAllowed must be a function',
        }
      }
      if (!config.tsgAvail || typeof config.tsgAvail !== 'function') {
        return {
          success: false,
          error: 'tsgAvail is required and must be a function'
        }
      }
      return {
        success: true,
      }
    }
  }
} as ExtensionPoint<TsgAccessAvailConfig>

export type SparkyNavItemAvailFn = (item: NavItem) => boolean
export type SparkyNavItemAvailConfig = {
  isNavItemAvail: SparkyNavItemAvailFn
  ignoreError?: boolean
  isAllowed?: IsAllowed
}
export const SPARKY_NAV_ITEM_AVAIL_EXTPOINT = {
  id: 'sparky.internal.nav.item.avail',
  version: '0.0.1',
  compatibilityRange: '~0.0.1',
  isSingleton: false,
  description: 'apply extra avail conditions for every nav items, works before nav v2 cleanup',
  validator: {
    validate(config) {
      if (!config) {
        return {
          success: false,
          error: 'nav item avail function configuration cannot be empty',
        }
      }
      if (!validateIsAllowed(config.isAllowed)) {
        return {
          success: false,
          error: 'optional isAllowed must be a function',
        }
      }
      if (!config.isNavItemAvail || typeof config.isNavItemAvail !== 'function' ||
        config.isNavItemAvail.length !== 1) {
        return {
          success: false,
          error: 'isNavItemAvail is required and must be a function that takes a single NavItem argument'
        }
      }
      if (config.ignoreError != null && typeof config.ignoreError !== 'boolean') {
        return {
          success: false,
          error: 'optional ignoreError must be a boolean'
        }
      }
      return {
        success: true,
      }
    }
  }
} as ExtensionPoint<SparkyNavItemAvailConfig>

export const initExtensionPoints = [
  SPARKY_LIBS_EXTPOINT,
  SPARKY_SERVICE_URL_PROVIDER_EXTPOINT,
  SPARKY_FEATURE_FLAG_PROVIDER_EXTPOINT,
  SPARKY_MFE_TSG_PREFILTER_EXTPOINT,
  SPARKY_MFE_TSG_VERSION_SELECT_EXTPOINT,
  SPARKY_TSG_MFE_PROPERTY_FALLBACK_EXTPOINT,
  SPARKY_TSG_ACCESS_AVAIL_EXTPOINT,
  SPARKY_NAV_ITEM_AVAIL_EXTPOINT,
  SPARKY_NAV3_LEFT_NAV_TPL_EXTPOINT,
]

export const extPointsExports = {
  SPARKY_LIBS_EXTPOINT,
  SPARKY_SERVICE_URL_PROVIDER_EXTPOINT,
  SPARKY_FEATURE_FLAG_PROVIDER_EXTPOINT,
  SPARKY_MFE_TSG_PREFILTER_EXTPOINT,
  SPARKY_MFE_TSG_VERSION_SELECT_EXTPOINT,
  SPARKY_TSG_MFE_PROPERTY_FALLBACK_EXTPOINT,
  SPARKY_TSG_ACCESS_AVAIL_EXTPOINT,
  SPARKY_NAV_ITEM_AVAIL_EXTPOINT,
  SPARKY_NAV2_ROUTES_EXTPOINT,
  SPARKY_NAV3_ROUTES_EXTPOINT,
  SPARKY_NAV3_REDIRECTS_EXTPOINT,
  SPARKY_NAV3_LEFT_NAV_EXTPOINT,
  SPARKY_NAV3_LEFT_NAV_TPL_EXTPOINT,
  SPARKY_NAV3_BOTTOM_LEFT_EXTPOINT,
  SPARKY_NAV3_BOTTOM_MOST_LEFT_EXTPOINT,
  SPARKY_NAV3_DYNAMIC_BOTTOM_LEFT_EXTPOINT,
  SPARKY_NAV3_DYNAMIC_TOP_LEFT_EXTPOINT,
  SPARKY_NAV3_TSG_STATE_LOADERS_EXTPOINT,
  SPARKY_NAV3_TSG_EXPOSES_COMPAT_EXTPOINT,
}

export const extpoints = [
  ...initExtensionPoints,
  SPARKY_NAV2_ROUTES_EXTPOINT,
  SPARKY_NAV3_ROUTES_EXTPOINT,
  SPARKY_NAV3_REDIRECTS_EXTPOINT,
  SPARKY_NAV3_LEFT_NAV_EXTPOINT,
  SPARKY_NAV3_BOTTOM_LEFT_EXTPOINT,
  SPARKY_NAV3_BOTTOM_MOST_LEFT_EXTPOINT,
  SPARKY_NAV3_DYNAMIC_BOTTOM_LEFT_EXTPOINT,
  SPARKY_NAV3_DYNAMIC_TOP_LEFT_EXTPOINT,
  SPARKY_NAV3_TSG_STATE_LOADERS_EXTPOINT,
  SPARKY_NAV3_TSG_EXPOSES_COMPAT_EXTPOINT,
]

export default extpoints // move object.assign to exports avoid side effect
